/**
 * fetch に timeout と AbortError の正規化を付与する。
 */

const TIMEOUT_ERROR_MESSAGE = 'timed out'

/**
 * timeout 用の Error を生成する。
 * @returns timeout を表す Error
 */
const createTimeoutError = (): Error => new Error(TIMEOUT_ERROR_MESSAGE)

/**
 * AbortError を判定する。
 * @param error - 判定対象の error
 * @returns AbortError の場合は true
 */
const isAbortError = (error: unknown): boolean =>
  (error instanceof DOMException || error instanceof Error) &&
  error.name === 'AbortError'

/**
 * timeout なしで fetch を実行し、AbortError を timed out に正規化する。
 * @param input - fetch の入力 URL
 * @param init - fetch 初期化オプション
 * @returns fetch の Response
 */
const runFetch = async (
  input: string,
  init: RequestInit,
): Promise<Response> => {
  try {
    return await fetch(input, init)
  } catch (error) {
    if (isAbortError(error)) {
      throw createTimeoutError()
    }
    throw error
  }
}

/**
 * 指定時間を超えた場合に abort する fetch を実行する。
 * @param input - fetch の入力 URL
 * @param init - fetch 初期化オプション
 * @param timeout - timeout ミリ秒
 * @returns fetch の Response
 */
export const fetchWithTimeout = async (
  input: string,
  init: RequestInit,
  timeout: number,
): Promise<Response> => {
  if (timeout <= 0) {
    return await runFetch(input, init)
  }

  const controller = new AbortController()
  const timeoutError = createTimeoutError()

  return await new Promise<Response>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      controller.abort()
      reject(timeoutError)
    }, timeout)

    void (async () => {
      try {
        const response = await fetch(input, {
          ...init,
          signal: controller.signal,
        })
        clearTimeout(timeoutId)
        resolve(response)
      } catch (error) {
        clearTimeout(timeoutId)
        if (isAbortError(error)) {
          reject(timeoutError)
          return
        }
        reject(error)
      }
    })()
  })
}
